import { assert } from '@deck.gl/core';
import * as turf from '@turf/turf';
import { Feature, LineString, Position } from 'geojson';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import type { FlightPlanRoutesHelper } from './flight-plan-routes-helper';
import type { FeatureIds } from './routes-layer';
import { FlightPlanRoute, ImpactLineSegment } from './types';

export const SOURCE = 'fe-impacts-source';
export const LINE = 'fe-impacts-line';
export const ImpactsLayerIds = [LINE];

interface ImpactFeatureProperty {
    impactGuid: string;
    recordId: number;
}

export class ImpactsHelper {
    private _show: boolean = true;
    private map: MaplibreMap | null = null;
    private readonly featureIds: Map<number, FeatureIds>;
    private readonly planned: FlightPlanRoutesHelper;

    constructor(
        featureIds: Map<number, FeatureIds>,
        planned: FlightPlanRoutesHelper,
        private readonly beforeId?: string
    ) {
        this.featureIds = featureIds;
        this.planned = planned;
    }

    get show(): boolean {
        return this._show;
    }

    set show(value: boolean) {
        this._show = value;
        this.showHide();
    }

    addToMap(map: MaplibreMap) {
        this.map = map;
        assert(this.map);

        this.map.addSource(SOURCE, {
            type: 'geojson',
            data: turf.featureCollection([])
        });

        this.map.addLayer(
            {
                id: LINE,
                type: 'line',
                source: SOURCE,
                paint: {
                    'line-width': 7,
                    'line-color': '#f00'
                }
            },
            this.beforeId
        );

        this.showHide();
    }

    removeFromMap() {
        assert(this.map);

        this.map.removeLayer(LINE);
        this.map.removeSource(SOURCE);
        this.map = null;
    }

    remove(id: number) {
        assert(this.map);

        const source = this.map.getSource(SOURCE) as GeoJSONSource;
        source.updateData({
            remove: this.featureIds.get(id)!.impacts
        });
    }

    removeAll() {
        const source = this.map?.getSource(SOURCE) as GeoJSONSource;
        source?.updateData({
            removeAll: true
        });
    }

    toRouteData(
        flightId: number,
        routeType: 'atc' | 'planned',
        route: FlightPlanRoute | null
    ): Feature<LineString, ImpactFeatureProperty>[] {
        if (!route) {
            return [];
        }
        const positions = alignImpactsWithRoute(route.impacts, route.waypoints);
        const features: Feature<LineString, ImpactFeatureProperty>[] = [];

        for (const [ndx, impactPoints] of positions.entries()) {
            if (impactPoints.length > 1) {
                features.push(
                    turf.lineString(
                        impactPoints,
                        {
                            impactGuid: route.impacts[ndx].id,
                            recordId: flightId,
                            type: routeType
                        },
                        {
                            id: `${flightId}-impacts-${routeType}-${ndx}-l`
                        }
                    )
                );
            }
        }
        return features;
    }

    showHide() {
        if (!this.map || !this.planned.settings) {
            return;
        }
        if (this.show) {
            const typeArray = [] as string[];
            if (this.planned.settings.showAtc) {
                typeArray.push('atc');
            }
            if (this.planned.settings.showPlanned) {
                typeArray.push('planned');
            }
            this.map.setFilter(LINE, ['in', ['get', 'type'], ['literal', typeArray]]);
        } else {
            this.map.setFilter(LINE, false);
        }
    }
}

function alignImpactsWithRoute(impacts: ImpactLineSegment[], route: { position: Position }[]): Position[][] {
    return impacts.map((x) => {
        const [p1, consumedWaypointsBegin] = findPointAtOffset(x.startPointIdx!, x.startPointOff, route);
        const [p2, consumedWaypointsEnd] = findPointAtOffset(x.resetPointIdx!, x.resetPointOff, route);

        const startPointIdx = x.startPointIdx! + consumedWaypointsBegin + 1; // startPointIdx is only a marker from which to start measuring startPointOff
        const endPointIdx = x.resetPointIdx! + consumedWaypointsEnd + 1; //add 1 because array.slice end is exclusive

        return [
            p1,
            ...route.slice(startPointIdx, endPointIdx).map((x) => x.position), // these are the waypoint between start of the impact and end of the impact
            p2
        ];
    });
}

function findPointAtOffset(startIdx: number, targetOffset: number, route: { position: Position }[]): [Position, number] {
    let currentPoint = route[startIdx].position;
    let cumOffset = 0;
    let consumedWaypoints = 0;
    for (let i = startIdx; i < route.length; i++) {
        if (i + 1 >= route.length) {
            break;
        }

        const nextPoint = route[i + 1].position;
        const distToNext = turf.distance(currentPoint, nextPoint, { units: 'nauticalmiles' });
        if (cumOffset + distToNext < targetOffset) {
            currentPoint = nextPoint;
            cumOffset += distToNext;
            consumedWaypoints++;
            continue;
        }

        const missingDistance = targetOffset - cumOffset;

        const bearing = turf.bearing(currentPoint, nextPoint);
        currentPoint = turf.destination(currentPoint, missingDistance, bearing, { units: 'nauticalmiles' }).geometry.coordinates;
        break;
    }

    return [currentPoint, consumedWaypoints];
}
